import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, inject, effect, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CACHE_STORAGE } from '@tvd/cdk/browser';
import { AuthService } from '../auth/auth.service';

export const CACHE_NAME = 'zynday-api-v1';

const CACHE_KEY_PREFIX = 'https://cache.local/';

/** Global cache store backed by the browser Cache API.
 *
 * Persists API responses across page reloads for offline support.
 * Falls back to no-ops when Cache API is unavailable (SSR, quota exceeded).
 */
@Injectable({
  providedIn: 'root',
})
export class CacheService {
  readonly #auth = inject(AuthService);
  readonly #isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  readonly #caches = inject(CACHE_STORAGE);
  #cachePromise: Promise<Cache> | null = null;
  #previousToken: string | undefined = undefined;
  /** Per-prefix invalidation counter. Bumped synchronously by
   * {@link deleteByPrefix} so an in-flight `set` can detect that its prefix was
   * invalidated mid-flight and avoid resurrecting a just-deleted key. */
  readonly #epochs = new Map<string, number>();

  readonly #tokenChange = effect(() => {
    const current = this.#auth.token();
    if (this.#previousToken === undefined) {
      this.#previousToken = current;
      return;
    }
    if (current !== this.#previousToken) {
      this.#previousToken = current;
      this.clear();
    }
  });

  #openCache(): Promise<Cache> {
    if (!this.#cachePromise) {
      this.#cachePromise = this.#caches.open(CACHE_NAME);
    }
    return this.#cachePromise;
  }

  #toCacheKey(key: string): string {
    return CACHE_KEY_PREFIX + encodeURIComponent(key);
  }

  #toResponse(httpRes: HttpResponse<unknown>): Response {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    for (const key of httpRes.headers.keys()) {
      headers[key] = httpRes.headers.get(key)!;
    }
    return new Response(JSON.stringify(httpRes.body), {
      status: httpRes.status,
      statusText: httpRes.statusText,
      headers,
    });
  }

  async #fromResponse(res: Response): Promise<HttpResponse<unknown>> {
    const body = await res.json();
    const headers: Record<string, string> = {};
    res.headers.forEach((value, key) => {
      headers[key] = value;
    });
    return new HttpResponse({
      body,
      status: res.status,
      statusText: res.statusText,
      headers: new HttpHeaders(headers),
    });
  }

  async get(key: string): Promise<HttpResponse<unknown> | undefined> {
    if (!this.#isBrowser) return undefined;
    try {
      const cache = await this.#openCache();
      const match = await cache.match(this.#toCacheKey(key));
      if (!match) return undefined;
      return this.#fromResponse(match);
    } catch {
      return undefined;
    }
  }

  /** Current invalidation epoch for a cache prefix (0 if never invalidated). */
  invalidationEpoch(prefix: string): number {
    return this.#epochs.get(prefix) ?? 0;
  }

  async set(
    key: string,
    value: HttpResponse<unknown>,
    prefix?: string,
    expectedEpoch?: number,
  ): Promise<void> {
    if (!this.#isBrowser) return;
    // The prefix was invalidated since this write's GET started — drop it so a
    // stale revalidation cannot resurrect a deleted key.
    if (prefix !== undefined && this.invalidationEpoch(prefix) !== expectedEpoch)
      return;
    try {
      const cache = await this.#openCache();
      await cache.put(this.#toCacheKey(key), this.#toResponse(value));
      // Belt-and-suspenders: if the prefix was invalidated *during* the write,
      // undo it — the awaited put may have landed after a concurrent
      // deleteByPrefix swept the cache.
      if (
        prefix !== undefined &&
        this.invalidationEpoch(prefix) !== expectedEpoch
      ) {
        await cache.delete(this.#toCacheKey(key));
      }
    } catch {
      // Degrade gracefully (quota exceeded, etc.)
    }
  }

  async delete(key: string): Promise<void> {
    if (!this.#isBrowser) return;
    try {
      const cache = await this.#openCache();
      await cache.delete(this.#toCacheKey(key));
    } catch {
      // Degrade gracefully
    }
  }

  async deleteByPrefix(prefixes: string[]): Promise<void> {
    // Bump the epoch for each prefix SYNCHRONOUSLY, before any await, so a
    // `set` racing with this invalidation observes the new epoch and is
    // ordered correctly against the delete below.
    for (const prefix of prefixes) {
      if (prefix) {
        this.#epochs.set(prefix, (this.#epochs.get(prefix) ?? 0) + 1);
      }
    }
    if (!this.#isBrowser) return;
    try {
      const cache = await this.#openCache();
      const keys = await cache.keys();
      for (const request of keys) {
        const raw = decodeURIComponent(
          request.url.slice(CACHE_KEY_PREFIX.length),
        );
        if (
          prefixes.some((prefix) => prefix && raw.startsWith(`${prefix}:::`))
        ) {
          await cache.delete(request);
        }
      }
    } catch {
      // Degrade gracefully
    }
  }

  async clear(): Promise<void> {
    if (!this.#isBrowser) return;
    try {
      await this.#caches.delete(CACHE_NAME);
      this.#cachePromise = null;
    } catch {
      // Degrade gracefully
    }
  }
}
